import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useOutletContext } from 'react-router-dom';
import { isEmpty } from '@rainbow-modules/validation';
import { ViewContextType } from 'pages/Opportunities/types';
import { EntityGet } from 'data/firestore/types';
import { Opportunity } from 'data/firestore/agent/opportunity/types';
import fetchDataForQuery from 'data/fetchHttpQuery';
import { AggregationTotals, OpportunitiesSortBy, SearchOpportunitiesResponse } from 'data/hooks/useSearchOpportunities/types';
import buildQueryKey from 'data/hooks/useSearchOpportunities/helpers/buildQueryKey';
import buildQueryPath from 'data/hooks/useSearchOpportunities/helpers/buildQueryPath';
import { SortOrder } from 'types';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { DEFAULT_SORT_OPPORTUNITIES } from '../../contants';

const useBoardOpportunitiesFromAlgolia = ({
    agentId,
    enabled = false,
}: {
    agentId: string;
    enabled: boolean;
}) => {
    const {
        selectedPipeline = '',
        stages = [],
        sortBy: sort = DEFAULT_SORT_OPPORTUNITIES,
        filters,
        searchQuery = '',
    } = useOutletContext<ViewContextType>();
    const queryClient = useQueryClient();
    const [aggregatedTotals, setAggregatedTotals] = useState<Record<string, AggregationTotals>>({});
    const [data, setData] = useState<Record<string, EntityGet<Opportunity>[]> | null>(null);
    const [isLoading, setLoading] = useState<Record<string, boolean>>({});

    // Debounce searchQuery to avoid excessive API calls
    const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);

    useEffect(
        () => {
            (async () => {
                if (enabled) {
                    const { stage: stageFilterValue } = filters || {};
                    const selectedStages = ((stageFilterValue ?? '') as string).split(',').filter((stageId) => !!stageId);
                    await Promise.all(
                        stages.map(async (stage) => {
                            if (
                                !isEmpty(selectedStages)
                                && !selectedStages.includes(stage.id)
                            ) {
                                setData((prev) => ({
                                    ...prev,
                                    [stage.id]: [],
                                }));
                                return;
                            }
                            setLoading((prevLoading) => ({ ...prevLoading, [stage.id]: true }));

                            const stageIds = stage?.id ? `${stage.id}` : filters?.stage as string;

                            try {
                                // eslint-disable-next-line max-len
                                const searchResults = await fetchDataForQuery<SearchOpportunitiesResponse>(
                                    queryClient,
                                    {
                                        key: buildQueryKey({
                                            agentId,
                                            searchQuery: debouncedSearchQuery,
                                            createdAt: filters?.createdAt as string,
                                            owner: filters?.owner as string,
                                            score: filters?.score as string,
                                            amount: filters?.amount as string,
                                            tags: filters?.tags as string,
                                            pipelineId: selectedPipeline as string,
                                            stageIds,
                                            sortBy: sort.sortBy as OpportunitiesSortBy,
                                            sortOrder: sort.order as SortOrder,
                                            pageSize: 100,
                                            page: 1,
                                        }),
                                        pathname: buildQueryPath({
                                            basePath: `/agents/${agentId}/opportunities/search`,
                                            filters: {
                                                searchQuery: debouncedSearchQuery,
                                                createdAt: filters?.createdAt as string,
                                                owner: filters?.owner as string,
                                                score: filters?.score as string,
                                                pipelineId: filters?.pipelineId as string,
                                                stageIds,
                                                amount: filters?.amount as string,
                                                tags: filters?.tags as string,
                                                sortBy: sort.sortBy as OpportunitiesSortBy,
                                                sortOrder: sort.order as SortOrder,
                                                pageSize: 100,
                                                page: 1,
                                            },
                                        }),
                                    },
                                );
                                setData((prev) => ({
                                    ...prev,
                                    [stage.id]: searchResults?.hits || [],
                                }));
                                setAggregatedTotals((prev) => ({
                                    ...prev,
                                    ...searchResults?.aggregations?.totals || {},
                                }));
                            } catch (err) {
                                setData(null);
                            }
                            setLoading(
                                (prevLoading) => ({
                                    ...prevLoading,
                                    [stage.id]: false,
                                }),
                            );
                        }),
                    );
                }
            })();
        },
        [
            debouncedSearchQuery,
            enabled,
            stages,
            agentId,
            selectedPipeline,
            filters,
            queryClient,
            sort,
        ],
    );

    return { isLoading, data, aggregatedTotals };
};

export default useBoardOpportunitiesFromAlgolia;
